import { ChangeDetectorRef, Component, inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from "@angular/core";
import { AsyncPipe, DatePipe, DecimalPipe, NgClass } from "@angular/common";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { TranslateModule } from "@ngx-translate/core";
import { PlacementManagerComponent } from "../../placement-manager/placement-manager.component";
import { FeatureFlagDirective } from "../../../../../../../shared/src/lib/directives/feature-flag.directive";
import { MatAccordion, MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle } from "@angular/material/expansion";
import { BetPlacementService } from "../../../../../../../shared/src/lib/services/bet-placement.service";
import { AhLadderPnlComponent } from "../../../content/market-layout/market/asian-handicap-pnl/ah-ladder-pnl/ah-ladder-pnl.component";
import { RouterLink } from "@angular/router";
import { BehaviorSubject, Subject } from "rxjs";
import { safeParseFloat } from "../../../../../../../shared/src/lib/utils/common";
import { Bet } from "../../../../../../../shared/src/lib/types/bet.type";
import { WhiteLabelConfigService } from "../../../../../../../shared/src/lib/services/white-label-config.service";
import { takeUntil } from "rxjs/operators";

type AggregatedBet = {
  selectionId: number;
  runnerName: string;
  totalStake: number;
  totalPnL: number;
  averageOdds: number;
};

type AggregatedBetsResult = {
  backBetsBySelection: AggregatedBet[];
  layBetsBySelection: AggregatedBet[];
  totalBackStake: number;
  totalLayStake: number;
};

@Component({
  selector: "app-matched-bets",
  imports: [
    NgClass,
    DatePipe,
    DecimalPipe,
    MatCheckboxModule,
    TranslateModule,
    PlacementManagerComponent,
    FeatureFlagDirective,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    AsyncPipe,
    AhLadderPnlComponent,
    RouterLink,
  ],
  templateUrl: "./matched-bets.component.html",
  styleUrl: "./matched-bets.component.scss",
})
export class MatchedBetsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() data: any[] = [];
  @Input() context: string = "";
  readonly destroy$ = new Subject<void>();
  cdr = inject(ChangeDetectorRef);
  matchedBets: any[] = [];
  betPlacementService = inject(BetPlacementService);
  averageOddsView$ = new BehaviorSubject<any[]>([]);
  wlConfigService = inject(WhiteLabelConfigService);
  marketAverageOdds = new Map();

  get isNonAsianHandicap() {
    return this.context !== "asian-handicap";
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["data"]) {
      this._init(changes["data"]?.currentValue);
    }
  }

  ngOnInit() {
    this._init(this.data);

    this.averageOddsView$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (markets) => {
        if (markets) {
          this.marketAverageOdds = new Map(markets.map((market) => [market?.marketId, this.generateAverageOdds(market)]));
        }
      },
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  _init(data: Bet[]) {
    const eventGroupedBets = this.groupByEvent(data);
    this.matchedBets = this.sortByLatestBet(eventGroupedBets);
    this.syncAverageOddBets(this.matchedBets);
  }

  syncAverageOddBets(updatedBets: any[]) {
    const currentEvents = this.averageOddsView$.getValue();
    if (!currentEvents?.length) return;

    const newEventBets = new Map(updatedBets?.map((betGroup: any) => [betGroup?.eventId, betGroup?.bets]));
    const updatedEventBets = currentEvents.map((event) => ({
      ...event,
      bets: newEventBets.get(event?.eventId) ?? event?.bets,
    }));

    this.averageOddsView$.next(updatedEventBets);
  }

  sortByLatestBet(data: any[]): any[] {
    return (
      [...data]
        // sort markets inside each event by their latestBet (desc)
        .map((event) => ({
          ...event,
          markets: Array.isArray(event.markets) ? [...event.markets].sort((a, b) => b.latestBet - a.latestBet) : event.markets,
        }))
        // then sort events by their latestBet (desc)
        .sort((a, b) => b.latestBet - a.latestBet)
    );
  }

  getLatestItem(items: any[]) {
    if (!items || items.length === 0) {
      return null; // Return null if the array is empty
    }

    let latestTime = 0; // Initialize with the earliest possible time (epoch)

    items.forEach((bet: any) => {
      const placedTime = new Date(bet.placedDate).getTime();
      if (placedTime > latestTime) {
        latestTime = placedTime;
      }
    });

    return latestTime;
  }

  groupByEvent(items: any[]) {
    const grouped = items.reduce(
      (acc, item) => {
        const eventName = item?.eventName?.trim();
        if (!eventName) return acc;

        const marketKeyRaw = item?.topic ?? item?.marketId;
        const marketKey = typeof marketKeyRaw === "string" ? marketKeyRaw.trim() : String(marketKeyRaw ?? "");
        if (!marketKey) return acc;

        const marketName = item?.marketName?.trim() ?? "Unknown Market";

        if (!acc[eventName]) {
          acc[eventName] = {
            eventId: item?.eventId,
            allBets: [] as any[],
            markets: {} as Record<string, { marketId: string; marketName: string; bets: any[] }>,
          };
        }

        // keep a flat list to compute event-level latestBet easily
        acc[eventName].allBets.push(item);

        // group by market inside each event
        if (!acc[eventName].markets[marketKey]) {
          acc[eventName].markets[marketKey] = {
            marketId: marketKey,
            marketName,
            bets: [],
          };
        }
        acc[eventName].markets[marketKey].bets.push(item);

        return acc;
      },
      {} as Record<
        string,
        {
          eventId: string;
          allBets: any[];
          markets: Record<string, { marketId: string; marketName: string; bets: any[] }>;
        }
      >,
    );

    return Object.keys(grouped).map((eventName) => {
      const { eventId, allBets, markets } = grouped[eventName];

      const marketsArr = Object.values(markets).map((market: any) => ({
        marketId: market?.marketId,
        marketName: market?.marketName,
        latestBet: this.getLatestItem(market?.bets),
        bets: market?.bets?.sort((a: any, b: any) => {
          const dateA: any = new Date(a?.matchedDate);
          const dateB: any = new Date(b?.matchedDate);
          return dateB - dateA;
        }),
      }));

      return {
        eventName,
        eventId,
        latestBet: this.getLatestItem(allBets),
        markets: marketsArr,
      };
    });
  }

  profitLoss(item: any) {
    return item?.side == "BACK" ? item?.potentialProfit : item?.potentialLoss;
  }

  stake(bet: any) {
    return bet?.stakeMatched > 0 && bet?.provider === "BF" && bet?.stakeRemaining === 0 ? bet?.stakeMatched : bet?.betSize;
  }

  setAverageOddsView(event: any, data: any) {
    let markets = this.averageOddsView$.getValue();
    if (event.target?.checked) {
      markets = [...markets, data];
    } else {
      markets = [...markets].filter((item) => item?.marketId !== data?.marketId);
    }
    this.averageOddsView$.next(markets);
  }

  generateAverageOdds(market: any): AggregatedBetsResult {
    const backMap = new Map<number, AggregatedBet>();
    const layMap = new Map<number, AggregatedBet>();
    let totalBackStake = 0;
    let totalLayStake = 0;

    for (const bet of market?.bets) {
      const selectionId = bet.selectionId;
      const isBack = bet.side === "BACK";
      const stake = this.stake(bet);
      const PnL = isBack ? safeParseFloat(bet.potentialProfit) : safeParseFloat(bet.potentialLoss);

      const targetMap = isBack ? backMap : layMap;

      const existing = targetMap.get(selectionId);

      if (existing) {
        existing.totalStake += stake;
        existing.totalPnL += PnL;
        existing.averageOdds = safeParseFloat(existing.totalPnL / existing.totalStake + 1);
      } else {
        targetMap.set(selectionId, {
          selectionId,
          runnerName: bet?.bettingType === "LINE" && bet?.descriptionPrefix !== "Fancy" ? bet?.marketName : bet?.runnerName,
          totalStake: stake,
          totalPnL: PnL,
          averageOdds: safeParseFloat(PnL / stake + 1),
        });
      }

      if (isBack) {
        totalBackStake += stake;
      } else {
        totalLayStake += stake;
      }
    }

    return {
      backBetsBySelection: Array.from(backMap.values()),
      layBetsBySelection: Array.from(layMap.values()),
      totalBackStake: safeParseFloat(totalBackStake),
      totalLayStake: safeParseFloat(totalLayStake),
    };
  }
}
